import { useState, useEffect } from 'react'
import { Button, Form, Card, Spinner, Alert, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBell, faBellSlash, faInfoCircle } from '@fortawesome/free-solid-svg-icons'
import {
  useIssueSubscriptions,
  useCreateIssueSubscription,
  useUpdateIssueSubscription,
  useDeleteIssueSubscription
} from 'api/hooks/use-issue-subscriptions'

const IssueSubscriptionToggle = ({ municipalityIssueId }) => {
  const { data: subscriptions, isLoading } = useIssueSubscriptions()
  const createSubscription = useCreateIssueSubscription()
  const updateSubscription = useUpdateIssueSubscription()
  const deleteSubscription = useDeleteIssueSubscription()

  const [subscription, setSubscription] = useState(null)
  const [isSubscribed, setIsSubscribed] = useState(false)
  const [error, setError] = useState(null)
  const [notificationSettings, setNotificationSettings] = useState({
    notify_on_new_log: true,
    notify_on_resolution: true,
    notify_on_snooze_expiration: true
  })

  useEffect(() => {
    if (subscriptions) {
      const existingSubscription = subscriptions.find(
        (sub) => sub.municipality_issue_id === municipalityIssueId
      )
      if (existingSubscription) {
        setSubscription(existingSubscription)
        setIsSubscribed(true)
        setNotificationSettings({
          notify_on_new_log: existingSubscription.notify_on_new_log,
          notify_on_resolution: existingSubscription.notify_on_resolution,
          notify_on_snooze_expiration: existingSubscription.notify_on_snooze_expiration
        })
      }
    }
  }, [subscriptions, municipalityIssueId])

  const handleToggleSubscription = async () => {
    setError(null)
    try {
      if (isSubscribed && subscription) {
        await deleteSubscription.mutateAsync(subscription.id)
        setIsSubscribed(false)
        setSubscription(null)
        setNotificationSettings({
          notify_on_new_log: true,
          notify_on_resolution: true,
          notify_on_snooze_expiration: true
        })
      } else {
        const result = await createSubscription.mutateAsync({
          municipality_issue_id: municipalityIssueId,
          ...notificationSettings
        })
        setIsSubscribed(true)
        setSubscription(result)
      }
    } catch (err) {
      console.error('Error toggling subscription:', err)
      setError(err.message || 'Failed to update subscription. Please try again.')
    }
  }

  const handleUpdateSettings = async (field, value) => {
    const updatedSettings = {
      ...notificationSettings,
      [field]: value
    }
    setNotificationSettings(updatedSettings)

    if (subscription) {
      setError(null)
      try {
        await updateSubscription.mutateAsync({
          subscriptionId: subscription.id,
          params: updatedSettings
        })
      } catch (err) {
        console.error('Error updating subscription settings:', err)
        setError(err.message || 'Failed to update settings. Please try again.')
      }
    }
  }

  if (isLoading) {
    return <Spinner animation="border" size="sm" />
  }

  return (
    <Card className="mb-3">
      <Card.Header className="bg-white">
        <strong>Notifications</strong>
        <OverlayTrigger
          placement="right"
          overlay={<Tooltip>Subscribe to receive email notifications for changes to this issue.</Tooltip>}
        >
          <span><FontAwesomeIcon icon={faInfoCircle} className="ms-2 text-muted" size="sm" /></span>
        </OverlayTrigger>
      </Card.Header>
      <Card.Body>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <div className="d-flex gap-2 mb-3">
          <Button
            variant={!isSubscribed ? 'primary' : 'outline-primary'}
            size="sm"
            onClick={() => isSubscribed && handleToggleSubscription()}
            disabled={
              createSubscription.isLoading ||
              updateSubscription.isLoading ||
              deleteSubscription.isLoading
            }
          >
            <FontAwesomeIcon icon={faBellSlash} className="me-1" />
            Off
          </Button>
          <Button
            variant={isSubscribed ? 'primary' : 'outline-primary'}
            size="sm"
            onClick={() => !isSubscribed && handleToggleSubscription()}
            disabled={
              createSubscription.isLoading ||
              updateSubscription.isLoading ||
              deleteSubscription.isLoading
            }
          >
            <FontAwesomeIcon icon={faBell} className="me-1" />
            On
          </Button>
        </div>

        <div className="p-3 bg-light rounded">
          <Form.Group className="mb-2">
            <Form.Check
              type="checkbox"
              id="notify-new-log"
              label="Notify me when new issue logs appear"
              checked={isSubscribed && notificationSettings.notify_on_new_log}
              disabled={!isSubscribed}
              onChange={(e) => handleUpdateSettings('notify_on_new_log', e.target.checked)}
            />
          </Form.Group>

          <Form.Group className="mb-2">
            <Form.Check
              type="checkbox"
              id="notify-resolution"
              label="Notify me when issue is resolved"
              checked={isSubscribed && notificationSettings.notify_on_resolution}
              disabled={!isSubscribed}
              onChange={(e) =>
                handleUpdateSettings('notify_on_resolution', e.target.checked)
              }
            />
          </Form.Group>

          <Form.Group className="mb-0">
            <Form.Check
              type="checkbox"
              id="notify-snooze"
              label="Notify me when snooze expires"
              checked={isSubscribed && notificationSettings.notify_on_snooze_expiration}
              disabled={!isSubscribed}
              onChange={(e) =>
                handleUpdateSettings('notify_on_snooze_expiration', e.target.checked)
              }
            />
          </Form.Group>
        </div>
      </Card.Body>
    </Card>
  )
}

export default IssueSubscriptionToggle
